<template>
  <a-space class="message-input">
    <a-textarea
      v-model:value="value"
      placeholder="Написать"
      show-count
      :maxlength="1000"
      :auto-size="{ minRows: 2, maxRows: 5 }"
      @keydown.enter="handleEnter"
    ></a-textarea>

    <a-tooltip
      title="К загрузке принимаются только файлы формата
      .pdf, .jpg, .png. Не более 3х файлов до 5Мб каждый."
    >
      <a-button
        class="attach"
        :icon="h(PaperClipOutlined)"
        @click="attachmentInputClicked"
      ></a-button>
    </a-tooltip>

    <a-tooltip :title="sendButtonTooltip">
      <a-button
        class="send"
        type="primary"
        @click="emit('send')"
        :disabled="isSendButtonDisabled"
        :icon="h(SendOutlined)"
      ></a-button>
    </a-tooltip>
  </a-space>
  <a-flex
    align="center"
    justify="flex-start"
    wrap="wrap"
    gap="small"
    v-if="decodedAttachments.length > 0"
  >
    <div
      v-for="(attachment, index) in decodedAttachments"
      :key="attachment.id"
      style="position: relative; display: inline-block; margin-bottom: 10px"
    >
      <img
        :src="attachment.contentMetaInfo! + ',' + attachment.content"
        alt="Attachment Preview"
        width="100px"
        height="100px"
        style="object-fit: contain"
        v-if="attachment.extension !== '.pdf'"
      />
      <FilePdfOutlined
        style="font-size: 100px; color: var(--primary-red-color)"
        v-else
      />

      <a-tooltip title="Удалить вложение">
        <a-button
          @click="deleteAttachment(index)"
          size="small"
          style="position: absolute; top: 0; right: 0"
        >
          <template #icon>
            <DeleteOutlined
              style="font-size: 15px; color: var(--primary-red-color)"
            />
          </template>
        </a-button>
      </a-tooltip>
    </div>
  </a-flex>
</template>

<script setup lang="ts">
import { h, watchEffect, useId } from "vue";
import {
  PaperClipOutlined,
  SendOutlined,
  DeleteOutlined,
  FilePdfOutlined,
} from "@ant-design/icons-vue";
import { createNotification } from "@/tools/notification";
import { useBase64 } from "@vueuse/core";
import type { BlobImageDto } from "@/api/api.dto";

const props = defineProps<{
  isSendButtonDisabled?: boolean;
  sendButtonTooltip?: string;
}>();

const value = defineModel<string>("value", { default: "" });
const attachments = defineModel<File[]>("attachments", { default: [] });
const decodedAttachments = defineModel<BlobImageDto[]>("decodedAttachments", {
  default: [],
});
const emit = defineEmits<{
  send: [];
}>();

const attachmentInputClicked = () => {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute(
    "accept",
    "image/png,image/jpeg,image/jpg,application/pdf",
  );
  input.setAttribute("multiple", "");

  input.onchange = () => {
    if (!input.files || input.files.length === 0) return;

    const newFiles = Array.from(input.files);

    if (attachments.value.length === 3) {
      createNotification("error", "Можно загрузить не более 3 файлов за раз");
      return;
    }

    const validFiles = newFiles.filter((file) => {
      const isValidType = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "application/pdf",
      ].includes(file.type);
      if (!isValidType) {
        createNotification(
          "error",
          `Файл ${file.name} не является PDF, PNG, JPG или JPEG`,
        );
      }

      const isValidSize = file.size <= 5 * 1024 * 1024;
      if (!isValidSize) {
        createNotification("error", `Файл ${file.name} превышает 5 МБ`);
      }

      return isValidType && isValidSize;
    });

    if (validFiles.length > 3) {
      createNotification(
        "error",
        "Общее количество файлов не должно превышать 3",
      );

      return;
    }

    attachments.value = [...attachments.value, ...validFiles];
  };

  input.click();
};

const handleEnter = (event: KeyboardEvent) => {
  if (!event.shiftKey && !event.ctrlKey && !event.altKey) {
    event.preventDefault();

    if (!props.isSendButtonDisabled) {
      emit("send");
    }
  }
};

const deleteAttachment = (index: number) => {
  attachments.value = attachments.value.filter((_, i) => i !== index);
};

watchEffect(async () => {
  const decoded = [];
  for (const attachment of attachments.value) {
    const { execute } = useBase64(attachment, { dataUrl: false });
    const content = await execute();
    const filename = attachment.name;
    const extension = filename.split(".").pop();

    if (content && extension) {
      const extWithDot = `.${extension}`;
      const splitContent = content.split(",");
      const contentMetaInfo = splitContent.at(0);
      const clearedContent = splitContent.at(1);
      if (clearedContent) {
        decoded.push({
          extension: extWithDot,
          content: clearedContent,
          contentMetaInfo,
          id: useId(),
        });
      }
    }
  }
  decodedAttachments.value = decoded;
});
</script>

<style scoped lang="scss">
.message-input {
  position: relative;

  :deep(.ant-space-item) {
    &:has(textarea) {
      width: 100%;

      textarea {
        padding-inline: 30px;
        scrollbar-width: none;
      }
    }

    &:has(button) {
      position: absolute;
      left: 0;
      top: 0;

      &:has(button.send) {
        left: unset;
        right: 0;

        .send:not([disabled]) {
          color: var(--color-primary);
        }
      }

      button {
        border: none;
        font-size: 17px;
        line-height: 1;
        padding: 0;
        background: none;
        box-shadow: none;
        color: var(--primary-white-color);

        &[disabled] {
          color: var(--primary-white-color);
        }

        &:hover:not([disabled]) {
          color: var(--color-primary);
        }
      }
    }
  }
}
</style>
